if (!customElements.get('cart-form')) {
  class CartForm extends BaseComponent {
    constructor() {
      super();
      this._debounceTimer = null;
      this._lastRequestTime = 0;
      this._MIN_REQUEST_GAP = 1500;
      this._DEBOUNCE_DELAY = 500;
    }

    init() {
      // Single listener on self — catches all bubbled quantity-change events from child <quantity-input> elements
      this.addEventListener('quantity-change', this._onQuantityChange.bind(this));

      // Delegated click listener for remove buttons
      this.addEventListener('click', this._onRemoveClick.bind(this));

      // Ensure Shopify.formatMoney exists
      this._ensureFormatMoney();
    }

    disconnectedCallback() {
      clearTimeout(this._debounceTimer);
    }

    // ── Event handlers ──

    _onQuantityChange(e) {
      const { key, quantity } = e.detail;
      const quantityInput = e.target.closest('quantity-input');
      if (!quantityInput) return;

      this._scheduleUpdate(quantityInput, key, quantity);
    }

    _onRemoveClick(e) {
      const removeBtn = e.target.closest('.cart-item__remove');
      if (!removeBtn) return;

      e.preventDefault();

      const key = removeBtn.dataset.key;
      if (!key) return;

      const cartItem = removeBtn.closest('.cart-item');
      if (!cartItem) return;

      // Prevent double-clicks
      if (removeBtn.dataset.removing === 'true') return;
      removeBtn.dataset.removing = 'true';

      // Visual feedback
      cartItem.style.opacity = '0.6';
      cartItem.style.pointerEvents = 'none';

      this._removeItem(key, cartItem);
    }

    // ── Debounced scheduler with cooldown ──

    _scheduleUpdate(quantityInput, key, quantity) {
      clearTimeout(this._debounceTimer);

      const timeSinceLast = Date.now() - this._lastRequestTime;
      const cooldownRemaining = Math.max(0, this._MIN_REQUEST_GAP - timeSinceLast);
      const delay = Math.max(this._DEBOUNCE_DELAY, cooldownRemaining);

      this._debounceTimer = setTimeout(() => {
        this._updateQuantity(quantityInput, key, quantity);
      }, delay);
    }

    // ── Core update function ──

    _updateQuantity(quantityInput, key, quantity, retryCount) {
      retryCount = retryCount || 0;

      if (quantityInput.loading) return;
      quantityInput.loading = true;

      quantity = Math.max(0, parseInt(quantity) || 0);
      this._lastRequestTime = Date.now();

      fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then(response => {
          if (response.status === 429) {
            throw { type: 'rate-limit', status: 429 };
          }
          if (!response.ok) throw new Error('Cart update failed');
          return response.json();
        })
        .then(cart => {
          // Item was decremented to 0 — remove it from the DOM
          if (quantity === 0) {
            const cartItem = quantityInput.closest('.cart-item');
            this._onItemRemoved(cart, cartItem);
            return;
          }

          // Sync input with server value to prevent desync
          const serverItem = cart.items.find(i => i.key === key);
          if (serverItem) {
            quantityInput.value = serverItem.quantity;

            const linePriceEl = document.getElementById(`LinePrice-${key}`);
            if (linePriceEl) {
              linePriceEl.innerHTML = this._formatMoney(serverItem.final_line_price, this.dataset.moneyFormat);
            }
          }

          this._updateTotals(cart);
          quantityInput.loading = false;
        })
        .catch(error => {
          if (error && error.type === 'rate-limit') {
            if (retryCount < 3) {
              const backoff = Math.pow(2, retryCount + 1) * 1000;
              console.warn(`Cart rate-limited. Retry ${retryCount + 1}/3 in ${backoff / 1000}s...`);
              setTimeout(() => {
                quantityInput.loading = false;
                this._lastRequestTime = Date.now();
                this._updateQuantity(quantityInput, key, quantity, retryCount + 1);
              }, backoff);
            } else {
              console.error('Cart update failed after 3 retries.');
              quantityInput.revert();
              quantityInput.loading = false;
            }
            return;
          }

          console.error('Error updating cart:', error);
          quantityInput.revert();
          quantityInput.loading = false;
        });
    }

    // ── Remove item via AJAX ──

    _removeItem(key, cartItem, retryCount) {
      retryCount = retryCount || 0;
      this._lastRequestTime = Date.now();

      fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id: key, quantity: 0 })
      })
        .then(response => {
          if (response.status === 429) {
            throw { type: 'rate-limit', status: 429 };
          }
          if (!response.ok) throw new Error('Cart remove failed');
          return response.json();
        })
        .then(cart => {
          this._onItemRemoved(cart, cartItem);
        })
        .catch(error => {
          if (error && error.type === 'rate-limit') {
            if (retryCount < 3) {
              const backoff = Math.pow(2, retryCount + 1) * 1000;
              console.warn(`Cart rate-limited. Retry ${retryCount + 1}/3 in ${backoff / 1000}s...`);
              setTimeout(() => {
                this._lastRequestTime = Date.now();
                this._removeItem(key, cartItem, retryCount + 1);
              }, backoff);
            } else {
              console.error('Cart remove failed after 3 retries.');
              cartItem.style.opacity = '';
              cartItem.style.pointerEvents = '';
              const removeBtn = cartItem.querySelector('.cart-item__remove');
              if (removeBtn) delete removeBtn.dataset.removing;
            }
            return;
          }

          console.error('Error removing cart item:', error);
          cartItem.style.opacity = '';
          cartItem.style.pointerEvents = '';
          const removeBtn = cartItem.querySelector('.cart-item__remove');
          if (removeBtn) delete removeBtn.dataset.removing;
        });
    }

    // ── Shared: handle item removal from DOM ──

    _onItemRemoved(cart, cartItem) {
      // If cart is now empty, reload to show the empty cart template
      if (cart.item_count === 0) {
        location.reload();
        return;
      }

      // Animate out the row then remove from DOM
      if (cartItem) {
        cartItem.style.transition = 'opacity 0.3s ease, max-height 0.3s ease';
        cartItem.style.opacity = '0';
        cartItem.style.overflow = 'hidden';
        cartItem.style.maxHeight = cartItem.offsetHeight + 'px';
        requestAnimationFrame(() => {
          cartItem.style.maxHeight = '0';
          cartItem.style.paddingTop = '0';
          cartItem.style.paddingBottom = '0';
          cartItem.style.marginTop = '0';
          cartItem.style.marginBottom = '0';
          cartItem.style.borderWidth = '0';
        });
        setTimeout(() => cartItem.remove(), 300);
      }

      this._updateTotals(cart);
    }

    // ── Shared: update total + pubsub ──

    _updateTotals(cart) {
      const totalEl = document.getElementById('CartTotal');
      if (totalEl) {
        totalEl.innerHTML = this._formatMoney(cart.total_price, this.dataset.moneyWithCurrencyFormat);
      }

      if (window.pubsub) {
        window.pubsub.publish('cart-update', cart);
      }
    }

    // ── Money formatting ──

    _ensureFormatMoney() {
      if (typeof Shopify === 'undefined') window.Shopify = {};
      if (!Shopify.formatMoney) {
        Shopify.formatMoney = this._formatMoney;
      }
    }

    _formatMoney(cents, format) {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = format || '${{amount}}';

      function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = (typeof precision === 'undefined') ? 2 : precision;
        thousands = (typeof thousands === 'undefined') ? ',' : thousands;
        decimal = (typeof decimal === 'undefined') ? '.' : decimal;

        if (isNaN(number) || number == null) return 0;

        number = (number / 100.0).toFixed(precision);
        const parts = number.split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        const centsStr = parts[1] ? (decimal + parts[1]) : '';
        return dollars + centsStr;
      }

      let value = '';
      switch (formatString.match(placeholderRegex)[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
      }

      return formatString.replace(placeholderRegex, value);
    }
  }

  customElements.define('cart-form', CartForm);
}
